/* eslint-disable no-const-assign */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import SearchField from "./DataTableSearch";
import DataTable, { createTheme } from "react-data-table-component";
import { toSnake } from "./ReqResConvertCase";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Button } from "reactstrap";
import TableSkeleton from "./Skeleton";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './DataTable.css';

const CommonDataTable = (props) => {
  const { layoutModeType } = useSelector((state) => ({
    layoutModeType: state.Layout.layoutModeType,
  }));

  createTheme("dark", {
    background: {
      default: "transparent",
    },
  });

  const [filterParams, setFilterParams] = useState(props.filterParams || {});

  useEffect(() => {
    if (props.filterParams) {
      setFilterParams(props.filterParams);
    }
  }, [props.filterParams]);


  const updateFilterParams = async (newFilterParams) => {
    setFilterParams(newFilterParams);
    await props.fetchData(newFilterParams);
  };

  const exportToExcel = async (e) => {
    e.preventDefault(); // Prevent form submission
    const newParams = { ...filterParams, isDownload: true };
    // Update filterParams to fetch all data
    await updateFilterParams(newParams);

    try {
      // Wait for the data to be fetched and available in props.data
      await new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          if (props.data && props.data.length > 0) {
            clearInterval(interval);
            resolve();
          }
        }, 500);
      });

      // Create worksheet from full data
      const ws = XLSX.utils.json_to_sheet(props.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      XLSX.writeFile(wb, `${props.exportFileName || "data"}.xlsx`, {
        bookType: "xlsx",
        type: "buffer",
        compression: true,
      });

      // Reset the isDownload param
      updateFilterParams({ ...filterParams, isDownload: false });
    } catch (error) {
      updateFilterParams({ ...filterParams, isDownload: false });
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const data = Array.isArray(props.data) ? props.data : [];

  // Sync internal selectedIds with props.selectedRows
  React.useEffect(() => {
    if (props.selectedRows && props.selectedRows.length === 0) {
      setSelectedIds([]);
    }
  }, [props.selectedRows]);

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = props.data.map(row => row.id);
      setSelectedIds(allIds);
      props.bulkSelected && props.bulkSelected(props.data);
    } else {
      setSelectedIds([]);
      props.bulkSelected && props.bulkSelected([]);
    }
  };

  const handleSelectRow = (rowId, row) => {
    const newSelectedIds = selectedIds.includes(rowId)
      ? selectedIds.filter(id => id !== rowId)
      : [...selectedIds, rowId];

    setSelectedIds(newSelectedIds);

    if (props.bulkSelected) {
      const selectedRows = props.data.filter(row => newSelectedIds.includes(row.id));
      props.bulkSelected(selectedRows);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !props.onRowReorder) return;

    const items = Array.from(data);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    props.onRowReorder(items);
  };

  const DraggableTable = () => {

    if (!props.data || props.data.length === 0) {
      console.warn("No data to display in DraggableTable");
    }
    return (
      <div className="draggable-table-wrapper">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="table-header">
            {props.checkboxEnabled && (
              <div className="header-cell checkbox-cell">
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  checked={selectedIds.length === props.data.length}
                />
              </div>
            )}
            <div className="header-cell drag-handle-cell"></div>
            {props.columns.map((column, index) => (
              <div key={index} className={`header-cell ${column.name.props.children === 'Actions' ? 'gender-cell' : ''}`}>
                {typeof column.name === 'string' ? (
                  <span className="font-weight-bold fs-13">{column.name}</span>
                ) : (
                  column.name
                )}
              </div>
            ))}
          </div>
          <Droppable droppableId="table">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="draggable-table-container"
              >
                {props.data.map((row, index) => (
                  <Draggable
                    key={row.id?.toString() || `row-${index}`}
                    draggableId={row.id?.toString() || `row-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`draggable-row ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        {props.checkboxEnabled && (
                          <div className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row.id)}
                              onChange={() => handleSelectRow(row.id, row)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                        <div {...provided.dragHandleProps} className="drag-handle">
                          <i className="ri-drag-move-2-fill" />
                        </div>
                        {props.columns.map((column, colIndex) => (
                          <div key={colIndex} className={`table-cell ${column.name.props.children === 'Actions' ? 'question-cell' : ''}`}>
                            {column.cell
                              ? column.cell(row)
                              : typeof column.selector === "function"
                                ? column.selector(row)
                                : row[column.selector]}
                          </div>
                        ))}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };



  const handleRowsPerPageChange = (newPerPage, page) => {
    const updatedFilterParams = {
      ...filterParams,
      currentPage: 1, // Reset to first page when changing rows per page
      page: 1,
      perPage: newPerPage,
      limit: newPerPage, // Add limit parameter for backend
    };
    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.selector) return;

    const orderBy = column.selector.toString().replace("row => row.", "").trim();

    const updatedFilterParams = {
      ...filterParams,
      order: sortDirection,
      orderBy: orderBy,
      sort: orderBy, // Add sort parameter for backend
      sortDirection: sortDirection,
    };

    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  const handlePageChange = (page) => {
    const updatedFilterParams = {
      ...filterParams,
      currentPage: page,
      page: page,
    };
    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  return (
    <Row>
      <Col lg={12}>
        <Card style={{ marginBottom: "60px" }}>
          <CardBody>
            <Row className="g-3">
              <div className="col-sm">
                <div className="datatable-toolbar">
                  <div className="datatable-toolbar-left">
                    {props.searchEnable && (
                      <div className="form-icon datatable-search-wrap">
                        <SearchField
                          filterParams={filterParams}
                          fetchData={props.fetchData}
                        />
                      </div>
                    )}
                    {props.customFilters && (
                      <div className="datatable-filter-wrap">
                        {props.customFilters}
                      </div>
                    )}
                  </div>
                  <div className="datatable-toolbar-actions">
                    {props.showAddButton && (
                      <Button
                        className="btn btn-primary add-button-margin"
                        color="primary"
                        type="submit"
                        onClick={props.addOnClickMethod}
                      >
                        <i className="ri-add-line"></i>
                        {`${props.buttonName ? props.buttonName : "New"}`}
                      </Button>
                    )}
                    {props.showExportButton && (
                      <Button
                        className="btn btn-primary"
                        color="primary"
                        type="submit"
                        onClick={props.onExport || exportToExcel}
                      >
                        <i className="bx bxs-file-export"></i>Export
                      </Button>
                    )}
                    {props.selectedRows?.length > 0 && (
                      <Button
                        className="btn btn-danger"
                        color="danger"
                        onClick={props.bulkDelete}
                      >
                        Delete Item
                      </Button>
                    )}
                  </div>
                </div>
                {props.filterComponent && (
                  <div className="mt-3">
                    {props.filterComponent}
                  </div>
                )}
              </div>
            </Row>
            <div className={`table-container table-responsive table-card ${props.onRowReorder ? 'draggable' : ''}`}>
              {props.onRowReorder ? (
                <DraggableTable />
              ) : (
                <DataTable
                  theme={layoutModeType === "light" ? "" : "dark"}
                  columns={props.columns}
                  data={props.data}
                  pagination
                  highlightOnHover
                  paginationServer={true}
                  selectableRows={props.checkboxEnabled}
                  progressComponent={
                    <div style={{ width: "100%" }}>
                      <TableSkeleton rows={10} columns={props.columns.length} />
                    </div>
                  }
                  progressPending={props.loading}
                  onChangeRowsPerPage={handleRowsPerPageChange}
                  sortServer={true}
                  pointerOnHover
                  onRowClicked={props.onRowClick}
                  persistTableHead
                  paginationPerPage={
                    filterParams.perPage || filterParams.limit || process.env.REACT_APP_RECORDS_PER_PAGE || 10
                  }
                  paginationDefaultPage={filterParams.currentPage || filterParams.page || 1}
                  paginationTotalRows={props.totalRows}
                  onSelectedRowsChange={({ selectedRows }) => {
                    if (props.bulkSelected) {
                      props.bulkSelected(selectedRows);
                    }
                  }}
                  onSort={handleSort}
                  onChangePage={handlePageChange}
                />
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default CommonDataTable;
